/**
 * Paid Breakdown N/A note appender — frontend mirror of
 * backend/services/paid_breakdown_na_notes.py
 *
 * Used by the Combined Tax Certification Notes preview in
 * JurisdictionManager to keep the on-screen note in sync with what the
 * PDF will render.
 *
 * Operates on a SINGLE jurisdiction (the notes preview is rendered per
 * jurisdiction in the fulfillment UI). The backend equivalent walks
 * the entire certificate; here we work with one jurisdiction at a time.
 *
 * Wording is locked to match backend exactly:
 *   "The tax collector confirmed that all <year> tax installments for
 *    <Jurisdiction Name> have been paid; however, an installment-level
 *    payment breakdown was not provided. Total amount paid: $<total>
 *    on <date>."
 */

const STEM_V2 = 'The tax collector confirmed that all';
const STEM_V1 = 'Payment breakdown was not provided by the tax collector';

const escapeRegExp = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const formatMoney = (value) => {
  const num = Number(value);
  if (!isFinite(num)) return '$0.00';
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (value) => {
  if (!value) return 'N/A';
  if (value instanceof Date) {
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${m}/${d}/${value.getFullYear()}`;
  }
  const s = String(value).trim();
  if (!s) return 'N/A';
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)) return s;
  const iso = s.split('T')[0];
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(iso);
  if (m) return `${m[2].padStart(2, '0')}/${m[3].padStart(2, '0')}/${m[1]}`;
  return s;
};

const formatYearsList = (years) => {
  if (!years || years.length === 0) return '';
  if (years.length === 1) return years[0];
  return `${years.slice(0, -1).join(', ')} and ${years[years.length - 1]}`;
};

const formatYearsPhrase = (years) => {
  // 'tax year 2026' / 'tax years 2026 through 2028' (contiguous) /
  // 'tax years 2024, 2026 and 2027' (non-contiguous)
  if (!years || years.length === 0) return '';
  if (years.length === 1) return `tax year ${years[0]}`;
  const nums = years.map((y) => parseInt(y, 10));
  const contiguous = nums.every((n) => Number.isFinite(n))
    && nums.slice(1).every((n, i) => n - nums[i] === 1);
  if (contiguous) return `tax years ${years[0]} through ${years[years.length - 1]}`;
  return `tax years ${formatYearsList(years)}`;
};

/**
 * Years covered by the jurisdiction-level Selected-Years lump-sum entry
 * (v2.6). Legacy v2.5 entries without a `covered_years` key cover every
 * year present in the installments. Selections are intersected with the
 * years actually present. Returns a Set; empty when absent/disabled.
 * Mirror of backend compute_covered_years().
 */
export const computeCoveredYears = (jurisdiction) => {
  const entry = jurisdiction && jurisdiction.paid_breakdown_na_all_years;
  if (!entry || typeof entry !== 'object' || !entry.enabled) return new Set();
  const instYears = new Set();
  for (const inst of jurisdiction.installments || []) {
    const y = String((inst && inst.tax_year) || '').trim();
    if (y) instYears.add(y);
  }
  if (Array.isArray(entry.covered_years)) {
    return new Set(
      entry.covered_years.map((y) => String(y).trim()).filter((y) => y && instYears.has(y))
    );
  }
  return instYears; // legacy v2.5 all-years entry
};

/**
 * Collect active Paid Breakdown N/A groups for a single jurisdiction.
 * Returns an array of { tax_year, jurisdiction_name, total_paid_amount, paid_date }.
 *
 * Source priority:
 *   0. jurisdiction.paid_breakdown_na_all_years.enabled === true (v2.6
 *      Selected-Years; legacy v2.5 entries without `covered_years` =
 *      all years) — single lump-sum group for the covered years. Years
 *      NOT covered remain eligible for the sources below.
 *   1. jurisdiction.paid_breakdown_na_groups[year].enabled === true (v2)
 *   2. Synthetic group from any installment with row-level
 *      paid_breakdown_na=true whose (year) has no v2 entry — sums
 *      paid_amount and picks first non-empty paid_date. (v1 BC)
 */
export const collectBreakdownNaGroupsForJurisdiction = (jurisdiction) => {
  if (!jurisdiction || typeof jurisdiction !== 'object') return [];
  const jurName = (jurisdiction.jurisdiction_name || jurisdiction.jurisdiction || '').trim();
  const out = [];

  // v2.6 Selected-Years — single lump-sum group for the covered years.
  const coveredSet = computeCoveredYears(jurisdiction);
  if (coveredSet.size > 0) {
    const allEntry = jurisdiction.paid_breakdown_na_all_years || {};
    const years = Array.from(coveredSet).sort();
    out.push({
      tax_year: formatYearsList(years),
      years,
      years_phrase: formatYearsPhrase(years),
      jurisdiction_name: jurName,
      total_paid_amount: allEntry.total_paid_amount ?? null,
      paid_date: allEntry.paid_date || '',
      selected_years: true,
    });
  }

  const explicit = jurisdiction.paid_breakdown_na_groups || {};
  const explicitYears = new Set();

  // v2 explicit — skipped for years covered by the lump sum
  for (const [year, entry] of Object.entries(explicit)) {
    if (!entry || typeof entry !== 'object' || !entry.enabled) continue;
    const yearS = String(year).trim();
    if (coveredSet.has(yearS)) continue;
    explicitYears.add(yearS);
    out.push({
      tax_year: yearS,
      jurisdiction_name: jurName,
      total_paid_amount: entry.total_paid_amount ?? null,
      paid_date: entry.paid_date || '',
    });
  }

  // v1 legacy fallback — skipped for covered/explicit years
  const legacyByYear = {};
  for (const inst of jurisdiction.installments || []) {
    if (!inst || !inst.paid_breakdown_na) continue;
    const yearS = String(inst.tax_year || '').trim();
    if (!yearS || explicitYears.has(yearS) || coveredSet.has(yearS)) continue;
    if (!legacyByYear[yearS]) legacyByYear[yearS] = [];
    legacyByYear[yearS].push(inst);
  }
  for (const [yearS, rows] of Object.entries(legacyByYear)) {
    let total = 0;
    let paidDate = '';
    for (const r of rows) {
      const n = parseFloat(r.paid_amount);
      if (isFinite(n)) total += n;
      if (!paidDate && r.paid_date) paidDate = r.paid_date;
    }
    out.push({
      tax_year: yearS,
      jurisdiction_name: jurName,
      total_paid_amount: total > 0 ? total : null,
      paid_date: paidDate,
    });
  }
  return out;
};

const stripPriorBlock = (notesText) => {
  if (!notesText) return notesText;
  let cleaned = notesText;
  const re1 = new RegExp(`\\n*${escapeRegExp(STEM_V2)}[^\\n]*(?:\\n(?!\\n)[^\\n]*)*`, 'gi');
  const re2 = new RegExp(`\\n*${escapeRegExp(STEM_V1)}[^\\n]*(?:\\n(?!\\n)[^\\n]*)*`, 'gi');
  cleaned = cleaned.replace(re1, '').replace(re2, '');
  return cleaned.replace(/\s+$/, '');
};

/**
 * Append the Paid Breakdown N/A clarifier(s) for a single jurisdiction
 * to ``notesText``. Strips both stale v1 and v2 copies first so
 * repeated calls + upgrades from v1 are idempotent.
 *
 * Returns ``notesText`` unchanged if no active groups exist.
 */
export const appendBreakdownNaNotes = (notesText, jurisdiction) => {
  const base = stripPriorBlock(notesText || '').replace(/\s+$/, '');
  const groups = collectBreakdownNaGroupsForJurisdiction(jurisdiction);
  if (groups.length === 0) return base;
  const lines = groups.map((g) => {
    const year = g.tax_year || 'the';
    const jur = g.jurisdiction_name || 'the jurisdiction';
    const amount = formatMoney(g.total_paid_amount);
    const date = formatDate(g.paid_date);
    if (g.selected_years) {
      // v2.6 Selected-Years wording (user-approved Jun 2026). Locked to
      // match backend/services/paid_breakdown_na_notes.py exactly.
      const yearsPhrase = g.years_phrase || 'the selected tax years';
      return (
        `The tax collector confirmed that all ${jur} tax installments ` +
        `for ${yearsPhrase} have been paid; however, a year-level or ` +
        `installment-level payment breakdown was not provided. ` +
        `Total amount paid: ${amount} on ${date}.`
      );
    }
    return (
      `The tax collector confirmed that all ${year} tax installments ` +
      `for ${jur} have been paid; however, an installment-level ` +
      `payment breakdown was not provided. ` +
      `Total amount paid: ${amount} on ${date}.`
    );
  });
  const block = lines.join('\n\n');
  return base ? `${base}\n\n${block}` : block;
};
